import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Heart, ShoppingBag } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import axios from 'axios';
import ProductCard from '../components/ProductCard';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const FavoritesPage = () => {
  const { isAuthenticated } = useAuth();
  const { t } = useLanguage();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!isAuthenticated) { setLoading(false); return; }
    const fetchFavorites = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/favorites`);
        setProducts(response.data);
      } catch (error) {
        // Error shown via loading=false + null data check in UI
      } finally {
        setLoading(false);
      }
    };
    fetchFavorites();
  }, [isAuthenticated]);

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-main pt-24 pb-16 flex flex-col items-center justify-center px-4">
        <Heart className="w-16 h-16 text-muted mb-4" />
        <h1 className="text-3xl text-heading font-light mb-4 text-center" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
          {t('favorites_login_title')}
        </h1>
        <p className="text-muted mb-8 text-center">{t('favorites_login_desc')}</p>
        <Link to="/connexion" className="btn-primary" data-testid="favorites-login-cta">{t('favorites_login_btn')}</Link>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-main pt-24 pb-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-8">
          <p className="text-[color:var(--color-primary)] text-xs uppercase tracking-[0.3em] mb-2">
            <Heart className="w-4 h-4 inline mr-1" /> {t('favorites_title')}
          </p>
          <h1 className="text-3xl sm:text-4xl text-heading font-light" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
            {t('favorites_saved')}
          </h1>
        </div>

        {loading ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
            {[...Array(8)].map((_, i) => (
              <div key={`fav-skeleton-${i}`} className="card-surface animate-pulse">
                <div className="aspect-square bg-surface" />
                <div className="p-3 space-y-2"><div className="h-2 bg-surface w-16" /><div className="h-3 bg-surface w-24" /></div>
              </div>
            ))}
          </div>
        ) : products.length > 0 ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4" data-testid="favorites-grid">
            {products.map(product => <ProductCard key={product.id} product={product} compact />)}
          </div>
        ) : (
          <div className="text-center py-20">
            <Heart className="w-12 h-12 text-muted mx-auto mb-4" />
            <p className="text-heading text-lg mb-2">{t('favorites_empty')}</p>
            <p className="text-muted mb-8">{t('favorites_empty_tap')}</p>
            <Link to="/catalogue" className="btn-primary inline-flex items-center gap-2" data-testid="favorites-browse-cta">
              <ShoppingBag className="w-4 h-4" /> {t('favorites_browse')}
            </Link>
          </div>
        )}
      </div>
    </div>
  );
};

export default FavoritesPage;
